import { useEffect, useMemo } from 'react';

import {
  useStreamRepositoryInjector,
  useBillingRepositoryInjector,
  usePointRepositoryInjector,
} from 'application/providers/DIContainerProvider';
import { useAppSelector } from 'application/providers/StoreProvider';
import { selectIsVideoAvailable } from 'application/store';
import { useAccountCompanyCountry, useAccountCompanyId, useAccountCompanyIsBillingEnabled } from 'application/hooks';

import useUserFeaturesOnboardingPass from 'domain/media/useUserFeaturesOnboardingPass';
import useUserFeaturesOnboarding from 'domain/media/useUserFeaturesOnboarding';
import { Country, UserFeatures } from 'domain/api/graphql/generated';

import { PlayerRadioType, UseSelectPlayerModalViewModel } from './types';
import { PLAYER_TEXT_TYPE } from './consts';
import { PlayerTypesInFlow } from '../types';

const useSelectPlayerModalViewModel = (): UseSelectPlayerModalViewModel => {
  const {
    isChangeStreamLoading,
    isCopyStreamTemplateLoading,
    isSavedStreamsLoading,
    changeStream,
    copyTemplateStream,
    getSavedStreams,
  } = useStreamRepositoryInjector();

  const { getBillingInfo } = useBillingRepositoryInjector();
  const { pointSetDevice } = usePointRepositoryInjector();

  const companyId = useAccountCompanyId();
  const companyCountry = useAccountCompanyCountry();
  const isBillingEnabled = useAccountCompanyIsBillingEnabled();

  const isVideoAvailable = useAppSelector(selectIsVideoAvailable);

  const { request: onboardingPassedRequest, result } = useUserFeaturesOnboardingPass();
  const { refetch: refetchFeaturesOnboarding } = useUserFeaturesOnboarding({
    features: [UserFeatures.WarningStreamFirstStart],
  });

  useEffect(() => {
    if (isBillingEnabled) {
      getBillingInfo(companyId);
    }
  }, []);

  const playersData = useMemo(() => {
    const players = [] as PlayerRadioType[];

    for (const type in PlayerTypesInFlow) {
      if (Object.prototype.hasOwnProperty.call(PlayerTypesInFlow, type)) {
        const playerType = type as PlayerTypesInFlow;

        const hideDevice =
          PLAYER_TEXT_TYPE[playerType].ruByOnly && !(companyCountry === Country.Ru || companyCountry === Country.By);

        const isHideVideoDevice = playerType === PlayerTypesInFlow.VIDEOPLAYER && !isVideoAvailable;

        if (!hideDevice && !isHideVideoDevice) {
          players.push({
            title: PLAYER_TEXT_TYPE[playerType].title,
            description: PLAYER_TEXT_TYPE[playerType].description,
            type: playerType,
          });
        }
      }
    }

    return players;
  }, []);

  return {
    playersData,
    isChangeStreamLoading,
    isCopyStreamTemplateLoading,
    isSavedStreamsLoading,
    isUserFeaturesOnboardingPassLoading: result.loading,
    changeStream,
    copyTemplateStream,
    getSavedStreams,
    onboardingPassedRequest,
    refetchFeaturesOnboarding,
    pointSetDevice,
  };
};

export default useSelectPlayerModalViewModel;
